import {
  type PayloadAction,
  createAsyncThunk,
  createSlice,
} from '@reduxjs/toolkit';
import { envVars } from '../../envVars';
import type {
  Address,
  ExtrajudicialNotification,
  Notification,
  NotificationDecision,
  PlatformId,
  UUIDString,
} from '../../types';
import { debouncePromise } from '../../utils/helpers';
import { headers } from '../../utils/requests/base-headers';
import type { Maybe, MaybeString } from '../../utils/types/maybe';
import {
  type BaseFetchProps,
  defaultBaseFetchProps,
  fetchGet,
  fetchPost,
  fetchPut,
} from '../utils';

type EjnSelected = BaseFetchProps & {
  ejnId: Maybe<UUIDString>;
  ejn: Maybe<ExtrajudicialNotification>;
  notificationId: Maybe<PlatformId>;
  notificationAddress: BaseFetchProps;
  decision: BaseFetchProps;
};

const initialState: EjnSelected = {
  ...defaultBaseFetchProps,
  ejnId: undefined,
  ejn: undefined,
  notificationId: undefined,
  notificationAddress: { ...defaultBaseFetchProps },
  decision: { ...defaultBaseFetchProps },
};

const debouncedFetchExtrajudicialNotification = debouncePromise(
  fetchGet<ExtrajudicialNotification>,
  300,
);

const debounceFetchUpdateNotificationAddress = debouncePromise(
  fetchPost<Notification>,
  300,
);

const debounceFetchUpdateNotificationDecision = debouncePromise(
  fetchPut<Notification>,
  300,
);

export const fetchExtrajudicialNotification = createAsyncThunk<
  ExtrajudicialNotification,
  UUIDString,
  { rejectValue: string }
>(
  'extrajudicialNotifications/getExtrajudicialNotificationById',
  async (id, thunkApi) => {
    const url = `${envVars.CREDITAS_HOST}/extrajudicial-notifications/${id}`;

    try {
      const response = await debouncedFetchExtrajudicialNotification(
        url,
        headers,
      );
      return response;
    } catch (_error) {
      return thunkApi.rejectWithValue(
        'Failed to fetch extrajudicial notifications.',
      );
    }
  },
);

export const fetchUpdateNotificationAddress = createAsyncThunk<
  Notification,
  { notificationId: string; address: Address },
  { rejectValue: string }
>(
  'extrajudicialNotifications/updateNotificationAddress',
  async ({ notificationId, address }, thunkApi) => {
    const url = `${envVars.CREDITAS_HOST}/notifications/${notificationId}/address`;

    try {
      const response = await debounceFetchUpdateNotificationAddress(
        url,
        address,
        headers,
      );
      return response;
    } catch (_error) {
      return thunkApi.rejectWithValue('Failed to post notification address.');
    }
  },
);

export const fetchUpdateNotificationDecision = createAsyncThunk<
  Notification,
  { notificationId: string; decision: NotificationDecision },
  { rejectValue: string }
>(
  'extrajudicialNotifications/updateNotificationDecision',
  async ({ notificationId, decision }, thunkApi) => {
    const url = `${envVars.CREDITAS_HOST}/notifications/${notificationId}/decision`;

    try {
      const response = await debounceFetchUpdateNotificationDecision(
        url,
        { decision },
        headers,
      );
      return response;
    } catch (_error) {
      return thunkApi.rejectWithValue('Failed to put notification decision.');
    }
  },
);

const extrajudicialNotificationSelectedSlice = createSlice({
  name: 'extrajudicialNotificationsFilters',
  initialState,
  reducers: {
    reset: () => initialState,
    resetNotificationAddressState: (state) => ({
      ...state,
      notificationAddress: {
        loading: false,
        success: undefined,
        error: null,
      },
    }),
    setEjnId: (_state, id: PayloadAction<MaybeString>) => ({
      ...initialState,
      ejnId: id.payload,
    }),
    setEjn: (state, ejn: PayloadAction<Maybe<ExtrajudicialNotification>>) => ({
      ...state,
      ejn: ejn.payload,
      notificationId: ejn.payload?.notification.id,
    }),
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchExtrajudicialNotification.pending, (state) => {
        state.ejn = undefined;
        state.loading = true;
        state.error = null;
        state.success = undefined;
      })
      .addCase(fetchExtrajudicialNotification.fulfilled, (state, action) => {
        state.ejn = action.payload;
        state.notificationId = action.payload.notification.id;
        state.loading = false;
        state.error = null;
        state.success = true;
      })
      .addCase(fetchExtrajudicialNotification.rejected, (state, action) => {
        state.ejn = undefined;
        state.success = false;
        state.loading = false;
        state.error = action.error.message || 'Something went wrong';
      })
      .addCase(fetchUpdateNotificationAddress.pending, (state) => {
        state.notificationAddress = {
          success: undefined,
          loading: true,
          error: null,
        };
      })
      .addCase(fetchUpdateNotificationAddress.fulfilled, (state, action) => {
        state.notificationAddress = {
          success: true,
          loading: false,
          error: null,
        };
        state.notificationId = action.payload.id;
        state.ejn = state.ejn
          ? { ...state.ejn, notification: action.payload }
          : undefined;
      })
      .addCase(fetchUpdateNotificationAddress.rejected, (state, action) => {
        state.notificationAddress = {
          success: false,
          loading: false,
          error: action.error.message || 'Something went wrong',
        };
      })
      .addCase(fetchUpdateNotificationDecision.pending, (state) => {
        state.decision = {
          success: undefined,
          loading: true,
          error: null,
        };
      })
      .addCase(fetchUpdateNotificationDecision.fulfilled, (state, action) => {
        state.decision = {
          success: true,
          loading: false,
          error: null,
        };
        state.notificationId = action.payload.id;
        state.ejn = state.ejn
          ? { ...state.ejn, notification: action.payload }
          : undefined;
      })
      .addCase(fetchUpdateNotificationDecision.rejected, (state, action) => {
        state.decision = {
          success: false,
          loading: false,
          error: action.error.message || 'Something went wrong',
        };
      });
  },
});

export const { reset, resetNotificationAddressState, setEjnId, setEjn } =
  extrajudicialNotificationSelectedSlice.actions;

export default extrajudicialNotificationSelectedSlice.reducer;
